import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Youtube } from "lucide-react";
import { useAuth } from "../App";

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ThreadsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.432 1.784 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.186.408-2.228 1.33-2.934.812-.622 1.904-.977 3.07-1.003.86-.019 1.656.085 2.394.266-.07-.63-.247-1.14-.535-1.523-.396-.527-1.001-.795-1.797-.795h-.065c-.673.013-1.253.207-1.724.577l-1.326-1.544c.78-.67 1.819-1.038 2.937-1.064h.095c1.34 0 2.378.446 3.088 1.325.614.762.948 1.797 1.01 3.06.518.196.996.438 1.426.732 1.323.906 2.214 2.199 2.58 3.741.468 1.97.148 4.22-1.609 5.986C18.403 23.025 15.792 23.978 12.186 24zm2.497-7.39c.156-.002.467-.03.795-.076-.036-.164-.084-.344-.147-.537-.143-.434-.41-.762-.774-.948-.377-.193-.862-.293-1.442-.293-1.123 0-1.987.373-2.43.827-.328.336-.399.698-.383.97.02.33.198.635.517.828.383.232.926.35 1.526.316.945-.052 1.636-.387 2.058-.862.096-.108.186-.23.28-.225z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { user, login } = useAuth();

  const footerLinks = {
    company: [
      { label: "About Us", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Submit a Story", href: "/submit-story" },
      { label: "Careers", href: "/contact" },
    ],
    content: [
      { label: "People", href: "/category/people" },
      { label: "Companies", href: "/category/companies" },
      { label: "Brands", href: "/category/brands" },
      { label: "Initiatives", href: "/category/initiatives" },
      { label: "Specials", href: "/category/specials" },
    ],
    more: [
      { label: "Flagship IPs", href: "/flagship-ips" },
      { label: "Advertise", href: "/contact" },
      { label: "Newsletter", href: "/#newsletter" },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com/peoplewhomatter.media", label: "Instagram" },
    { icon: ThreadsIcon, href: "https://threads.net/@peoplewhomatter.media", label: "Threads" },
    { icon: XIcon, href: "https://x.com/PwM_Media", label: "X" },
    { icon: Facebook, href: "https://facebook.com/peoplewhomattermedia", label: "Facebook" },
    { icon: Linkedin, href: "https://linkedin.com/company/people-who-matter", label: "LinkedIn" },
    { icon: Youtube, href: "https://youtube.com/@PeopleWhoMatterMedia", label: "YouTube" },
  ];

  return (
    <footer className="bg-[#050505] text-white noise-overlay" data-testid="main-footer">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <img
                src="/static/img/logo-white.svg"
                alt="People Who Matter"
                width="5516"
                height="1107"
                className="h-12 md:h-14 w-auto"
              />
            </Link>
            <p className="text-neutral-500 text-sm mb-4 max-w-xs leading-relaxed">
              A technology enabled digital editorial platform focused on the people, brands, companies and initiatives shaping business, culture and contemporary India.
            </p>
            <a href="mailto:hello@peoplewhomatter.in" className="text-neutral-400 hover:text-white transition-colors text-sm" data-testid="footer-email">
              hello@peoplewhomatter.in
            </a>
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center text-neutral-500 hover:bg-white hover:text-black transition-all duration-300"
                  style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-sans text-[0.65rem] font-bold tracking-[0.2em] uppercase text-neutral-600 mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-sans text-[0.65rem] font-bold tracking-[0.2em] uppercase text-neutral-600 mb-6">Content</h3>
            <ul className="space-y-3">
              {footerLinks.content.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-sans text-[0.65rem] font-bold tracking-[0.2em] uppercase text-neutral-600 mb-6">More</h3>
            <ul className="space-y-3">
              {footerLinks.more.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Large typographic brand mark */}
        <div className="mt-20 mb-12 overflow-hidden">
          <p className="font-serif font-black text-neutral-900 leading-none select-none" style={{ fontSize: 'clamp(3rem, 10vw, 8rem)' }}>
            People Who Matter
          </p>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-neutral-600 text-xs text-center md:text-left">
            &copy; {currentYear} People Who Matter. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/privacy-policy" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-privacy-policy">
              Privacy Policy
            </Link>
            <Link to="/editorial-standards" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-editorial-standards">
              Editorial Standards
            </Link>
            <Link to="/media-kit" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-media-kit">
              Media Kit
            </Link>
            <Link to="/disclaimer" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-disclaimer">
              Disclaimer &amp; Editorial Policy
            </Link>
            <Link to="/terms" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-terms">
              Terms of Use
            </Link>
            <Link to="/sitemap" className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap" data-testid="footer-sitemap">
              Sitemap
            </Link>
            <a href="/api/rss.xml" className="text-neutral-600 hover:text-white transition-colors text-xs inline-flex items-center gap-1 whitespace-nowrap" data-testid="footer-rss" title="Subscribe via RSS" target="_blank" rel="noopener">
              <span aria-hidden>📡</span> RSS
            </a>
            {user ? (
              <Link
                to="/admin"
                className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap"
                data-testid="footer-admin-link"
              >
                Admin
              </Link>
            ) : (
              <button
                onClick={login}
                className="text-neutral-600 hover:text-white transition-colors text-xs whitespace-nowrap"
                data-testid="footer-login-button"
              >
                Admin
              </button>
            )}
            <p className="text-neutral-600 text-xs whitespace-nowrap">
              A <a href="https://www.nexbrands.in" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors">NexBrands Inc</a> Initiative
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
